const DEFAULT_APP_PROXY_PREFIX = "/apps/sigma-stock-alerts";
const APP_PROXY_PREFIX_PATTERN = /^\/apps\/[a-z0-9](?:[a-z0-9-]{0,62})$/u;
const API_ENDPOINTS = new Set(["config", "signup", "status", "spin", "recover"]);
const FALLBACK_ENDS_AT = "2026-10-19T02:00:00.000Z";
const DISPLAY_DELAY_MS = 10_000;
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE = {
  browser: "sigmaWheel2026:browser",
  firstSeen: "sigmaWheel2026:firstSeen",
  dismissed: "sigmaWheel2026:dismissed",
  submitted: "sigmaWheel2026:submitted",
  claim: "sigmaWheel2026:claimToken",
  claimed: "sigmaWheel2026:claimedToken",
  launcherPosition: "sigmaWheel2026:launcherPosition",
  launcherDismissal: "sigmaWheel2026:launcherDismissal",
};
const VALID_REWARDS = new Set([5, 7, 10, 15, 20]);
const WHEEL_INDEX = new Map([[20, 0], [5, 1], [10, 2], [15, 3], [7, 4]]);

function normalizeAppProxyPrefix(value) {
  const prefix = typeof value === "string" ? value.trim() : "";
  return APP_PROXY_PREFIX_PATTERN.test(prefix) ? prefix : DEFAULT_APP_PROXY_PREFIX;
}

function storageRead(storageName, key) {
  try { return { available: true, value: window[storageName].getItem(key) }; }
  catch { return { available: false, value: null }; }
}

function storageWrite(storageName, key, value) {
  try { window[storageName].setItem(key, value); return true; } catch { return false; }
}

function storageRemove(storageName, key) {
  try { window[storageName].removeItem(key); } catch { /* Server state remains authoritative. */ }
}

function createRecoveryToken() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function errorMessage(code, fallback) {
  const messages = {
    invalid_request: "Enter a valid email address to continue.",
    already_claimed: "This email has already received a launch reward.",
    rate_limited: "Too many attempts. Wait a moment, then try again.",
    consent_unavailable: "We could not complete email signup right now. Please try again.",
    reward_unavailable: "Your reward is reserved, but the code is not ready yet. Try again shortly.",
    campaign_ended: "This launch reward has ended.",
    unavailable: "The launch reward is temporarily unavailable.",
    unauthorized: "The launch reward could not be opened from this page.",
    not_found: "This reward session could not be recovered.",
  };
  return messages[code] || fallback || "Something went wrong. Please try again.";
}

async function api(apiBase, path, options = {}) {
  if (!API_ENDPOINTS.has(path)) throw new Error("Invalid wheel API endpoint");
  const headers = { Accept: "application/json", ...options.headers };
  const response = await fetch(`${apiBase}/${path}`, { ...options, headers, credentials: "same-origin", cache: "no-store" });
  let data;
  try { data = await response.json(); } catch { data = null; }
  if (!response.ok || !data?.ok) {
    const failure = new Error(errorMessage(data?.code, data?.message));
    failure.code = data?.code || `http_${response.status}`;
    failure.status = response.status;
    throw failure;
  }
  return data;
}

function post(apiBase, path, body) {
  return api(apiBase, path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

class SigmaLaunchWheel extends HTMLElement {
  constructor() {
    super();
    this.apiBase = `${DEFAULT_APP_PROXY_PREFIX}/wheel/v1`;
    this.campaign = null;
    this.lastFocus = null;
    this.isOpen = false;
    this.pendingOpen = false;
    this.submitted = storageRead("localStorage", STORAGE.submitted).value === "true";
    const storedClaim = storageRead("localStorage", STORAGE.claim);
    const previousClaim = storageRead("sessionStorage", STORAGE.claim);
    const validToken = (value) => /^[A-Za-z0-9_-]{43}$/u.test(value || "");
    this.claimToken = validToken(previousClaim.value) ? previousClaim.value
      : validToken(storedClaim.value) ? storedClaim.value : null;
    this.claimStorageAvailable = storedClaim.available || previousClaim.available;
    if (this.claimToken) storageWrite("localStorage", STORAGE.claim, this.claimToken);
    this.hasClaimedReward = Boolean(this.claimToken && (
      storageRead("localStorage", STORAGE.claimed).value === this.claimToken
      || storageRead("sessionStorage", STORAGE.claimed).value === this.claimToken));
    this.accountRecovery = false;
    this.recoveryReturn = new URL(window.location.href).searchParams.get("launch_reward") === "recover";
    this.launcherRequested = false;
    this.launcherSuppressed = false;
    this.isLauncherDismissalConfirmation = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.launcherDrag = null;
    this.savedLauncherPosition = null;
    this.launcherClickSuppressedUntil = 0;
    this.spinPending = false;
    this.priorityTimer = null;
    this.deadlineTimer = null;
    this.priorityObserver = null;
    this.ownsRootScrollLock = false;
    this.backgroundInertStates = new Map();
    this.pendingConsentTimer = null;
    this.copyFeedbackTimer = null;
    this.lifecycleRevision = 0;
  }

  connectedCallback() {
    this.apiBase = `${normalizeAppProxyPrefix(this.dataset.appProxyPrefix)}/wheel/v1`;
    this.render();
    this.cacheElements();
    this.bindEvents();
    this.observePrioritySurfaces();
    this.restoreLauncherPosition();
    this.boot();
  }

  disconnectedCallback() {
    clearTimeout(this.priorityTimer);
    clearTimeout(this.deadlineTimer);
    clearTimeout(this.pendingConsentTimer);
    clearTimeout(this.copyFeedbackTimer);
    this.priorityObserver?.disconnect();
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onViewportChange);
    window.visualViewport?.removeEventListener("resize", this.onViewportChange);
    window.visualViewport?.removeEventListener("scroll", this.onViewportChange);
    this.finishLauncherDrag(false);
    this.restoreBackgroundInteraction();
    this.restoreRootScroll();
  }

  render() {
    this.innerHTML = `
      <div class="sigma-wheel" data-root>
        <div class="sigma-wheel__backdrop" data-backdrop hidden>
          <section class="sigma-wheel__dialog" role="dialog" aria-modal="true" aria-labelledby="sigma-wheel-title" tabindex="-1">
            <span class="sigma-wheel__brand" role="img" aria-label="Sigma Coffee"></span>
            <button class="sigma-wheel__close" type="button" data-close aria-label="Close launch reward">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5 19 19M19 5 5 19"/></svg>
            </button>

            <div class="sigma-wheel__screen" data-screen="signup">
              <h1 class="sigma-wheel__title" id="sigma-wheel-title">Celebrate our new<br>storefront launch!</h1>
              <p class="sigma-wheel__copy">Join our emails for fresh coffee, new arrivals and offers, then spin for a launch reward. By selecting ‘Sign up to spin’, you agree to receive marketing emails from Sigma Coffee. Unsubscribe anytime.</p>
              <form data-signup novalidate>
                <div style="position:absolute;width:1px;height:1px;overflow:hidden;clip-path:inset(50%);pointer-events:none" aria-hidden="true">
                  <label>Website<input type="text" name="website" data-website tabindex="-1" autocomplete="off"></label>
                </div>
                <label class="sigma-wheel__field">
                  <span class="sigma-wheel__label">Email</span>
                  <input class="sigma-wheel__input" data-email type="email" name="email" placeholder="Your email here" autocomplete="email" inputmode="email" aria-describedby="sigma-wheel-signup-error" required>
                </label>
                <button class="sigma-wheel__button" data-signup-button type="submit">Sign up to spin</button>
                <p class="sigma-wheel__error" id="sigma-wheel-signup-error" data-signup-error role="alert"></p>
              </form>
            </div>

            <div class="sigma-wheel__screen" data-screen="wheel" hidden>
              <h1 class="sigma-wheel__title">Give it a spin!</h1>
              <p class="sigma-wheel__copy sigma-wheel__copy--lead">Your launch reward is one spin away! Win a discount on eligible products. Preorders and selected equipment are excluded.</p>
              <p class="sigma-wheel__copy sigma-wheel__copy--note">For new orders only.</p>
              <div class="sigma-wheel__wheel-wrap" aria-hidden="true">
                <div class="sigma-wheel__pin"></div>
                <div class="sigma-wheel__wheel" data-wheel>
                  <span class="sigma-wheel__slice" style="--slice-angle:0deg"><span>20% off</span></span>
                  <span class="sigma-wheel__slice" style="--slice-angle:72deg"><span>5% off</span></span>
                  <span class="sigma-wheel__slice" style="--slice-angle:144deg"><span>10% off</span></span>
                  <span class="sigma-wheel__slice" style="--slice-angle:216deg"><span>15% off</span></span>
                  <span class="sigma-wheel__slice" style="--slice-angle:288deg"><span>7% off</span></span>
                </div>
              </div>
              <div class="sigma-wheel__actions">
                <button class="sigma-wheel__button" data-spin type="button">Spin for my reward</button>
                <a class="sigma-wheel__button sigma-wheel__button--secondary" href="/collections/thelibrary?filter.v.availability=1&sort_by=created-descending" data-continue>Continue shopping</a>
              </div>
              <p class="sigma-wheel__error" data-spin-error role="alert"></p>
            </div>

            <div class="sigma-wheel__screen" data-screen="result" hidden>
              <h1 class="sigma-wheel__title" data-result-title>You won!</h1>
              <p class="sigma-wheel__copy" data-result-copy></p>
              <p class="sigma-wheel__copy">Meet Sigma Rewards: create an account or log in to start collecting rewards.</p>
              <div class="sigma-wheel__coupon">
                <bdi class="sigma-wheel__code" data-code></bdi>
                <button class="sigma-wheel__copy-button" type="button" data-copy aria-label="Copy discount code">
                  <svg class="sigma-wheel__copy-icon sigma-wheel__copy-icon--copy" viewBox="0 0 24 24" aria-hidden="true"><rect x="8" y="8" width="12" height="13" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h3"/></svg>
                  <svg class="sigma-wheel__copy-icon sigma-wheel__copy-icon--check" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>
                </button>
              </div>
              <p class="sigma-wheel__feedback" data-copy-feedback role="status" hidden>Code copied!</p>
              <a class="sigma-wheel__button" href="/collections/thelibrary?filter.v.availability=1&sort_by=created-descending" data-continue>Continue shopping</a>
              <a class="sigma-wheel__button sigma-wheel__button--secondary" href="/account/login" data-k-hub-ignore data-continue>Create account or log in</a>
            </div>

            <div class="sigma-wheel__screen sigma-wheel__status sigma-wheel__loading" data-screen="loading" hidden>
              <div class="sigma-wheel__loading-mark" aria-hidden="true"></div>
              <h1 class="sigma-wheel__title" data-loading-title>Loading your reward</h1>
              <p class="sigma-wheel__copy" data-loading-copy role="status" aria-live="polite">Checking your secure reward session…</p>
            </div>

            <div class="sigma-wheel__screen sigma-wheel__status" data-screen="status" hidden>
              <h1 class="sigma-wheel__title" data-status-title>Launch reward unavailable</h1>
              <p class="sigma-wheel__copy" data-status-copy></p>
              <button class="sigma-wheel__button" data-retry type="button">Try again</button>
              <button class="sigma-wheel__button" data-recover type="button" hidden>Recover my code</button>
              <a class="sigma-wheel__button" data-recovery-login data-k-hub-ignore hidden>Sign in to recover my code</a>
              <a class="sigma-wheel__button" href="/pages/contact" data-status-support hidden>Contact us</a>
              <a class="sigma-wheel__button sigma-wheel__button--secondary" href="/collections/thelibrary?filter.v.availability=1&sort_by=created-descending" data-continue>Continue shopping</a>
            </div>

            <div class="sigma-wheel__screen sigma-wheel__status" data-screen="launcher-dismissal" hidden>
              <h1 class="sigma-wheel__title" data-launcher-dismissal-title>Hide the spin button?</h1>
              <p class="sigma-wheel__copy" data-launcher-dismissal-copy>If you dismiss this button, you may miss your chance to spin for a launch discount. The offer ends Monday 19 October at 3am UK time.</p>
              <button class="sigma-wheel__button" data-launcher-dismissal-cancel type="button">Keep my chance to spin</button>
              <button class="sigma-wheel__button sigma-wheel__button--secondary" data-launcher-dismissal-confirm type="button">Dismiss</button>
              <p class="sigma-wheel__error" data-launcher-dismissal-error role="alert"></p>
            </div>
          </section>
        </div>

        <div class="sigma-wheel__launcher" data-launcher hidden>
          <div class="sigma-wheel__launcher-pill">
            <button class="sigma-wheel__teaser" data-teaser type="button" aria-label="Spin to win" aria-keyshortcuts="Home">
              <svg viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="13" fill="none" stroke="currentColor" stroke-width="2"/><path d="M16 3v13L27 9M16 16 6 24M16 16l3 13" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="16" cy="16" r="3" fill="currentColor"/></svg>
              <span data-launcher-label>Spin to win</span>
            </button>
            <button class="sigma-wheel__launcher-dismiss site-close-button" data-launcher-dismiss type="button" aria-label="Hide spin button">
              <span class="site-close-button__surface" aria-hidden="true">
                <span class="site-close-button__icon">
                  <svg preserveAspectRatio="none" width="100%" height="100%" overflow="visible" style="display:block" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.25 6.25L17.75 17.75M17.75 6.25L6.25 17.75" stroke="var(--stroke-0, currentColor)" stroke-width="1.5" stroke-linecap="round"/></svg>
                </span>
              </span>
            </button>
          </div>
          <button class="sigma-wheel__launcher-reset" data-launcher-reset type="button" aria-label="Reset button position" hidden>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" data-central-icon="IconStepBack"><path d="M6.75 3.25L3.75 6.25L6.75 9.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4.5 6.25H14.25C17.7018 6.25 20.5 9.04822 20.5 12.5C20.5 15.9518 17.7018 18.75 14.25 18.75H5.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>`;
  }

  cacheElements() {
    this.backdrop = this.querySelector("[data-backdrop]");
    this.dialog = this.querySelector("[role=dialog]");
    this.launcher = this.querySelector("[data-launcher]");
    this.teaser = this.querySelector("[data-teaser]");
    this.launcherLabel = this.querySelector("[data-launcher-label]");
    this.launcherDismiss = this.querySelector("[data-launcher-dismiss]");
    this.launcherReset = this.querySelector("[data-launcher-reset]");
    this.launcherDismissalCopy = this.querySelector("[data-launcher-dismissal-copy]");
    this.launcherDismissalTitle = this.querySelector("[data-launcher-dismissal-title]");
    this.launcherDismissalCancel = this.querySelector("[data-launcher-dismissal-cancel]");
    this.launcherDismissalConfirm = this.querySelector("[data-launcher-dismissal-confirm]");
    this.launcherDismissalError = this.querySelector("[data-launcher-dismissal-error]");
    this.screens = [...this.querySelectorAll("[data-screen]")];
    this.signupForm = this.querySelector("[data-signup]");
    this.emailInput = this.querySelector("[data-email]");
    this.signupButton = this.querySelector("[data-signup-button]");
    this.signupError = this.querySelector("[data-signup-error]");
    this.spinButton = this.querySelector("[data-spin]");
    this.spinError = this.querySelector("[data-spin-error]");
    this.wheel = this.querySelector("[data-wheel]");
    this.retryButton = this.querySelector("[data-retry]");
    this.statusSupport = this.querySelector("[data-status-support]");
    this.recoveryButton = this.querySelector("[data-recover]");
    this.recoveryLogin = this.querySelector("[data-recovery-login]");
    // Liquid supplies Shopify's account route. Never accept a return URL from query input.
    this.recoveryLogin.href = this.dataset.recoveryLoginUrl || "/account/login";
    this.copyButton = this.querySelector("[data-copy]");
    this.onKeydown = this.handleKeydown.bind(this);
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.onLauncherPointerDown = this.handleLauncherPointerDown.bind(this);
    this.onLauncherPointerMove = this.handleLauncherPointerMove.bind(this);
    this.onLauncherPointerUp = this.handleLauncherPointerUp.bind(this);
    this.onLauncherPointerCancel = this.handleLauncherPointerCancel.bind(this);
    this.updateLauncherLabel();
  }

  bindEvents() {
    this.querySelector("[data-close]").addEventListener("click", () => this.requestClose());
    this.backdrop.addEventListener("click", (event) => { if (event.target === this.backdrop) this.requestClose(); });
    this.teaser.addEventListener("click", (event) => {
      if (event.detail !== 0 && Date.now() < this.launcherClickSuppressedUntil) {
        event.preventDefault();
        this.launcherClickSuppressedUntil = 0;
        return;
      }
      this.open(true);
    });
    this.teaser.addEventListener("pointerdown", this.onLauncherPointerDown);
    this.teaser.addEventListener("pointermove", this.onLauncherPointerMove);
    this.teaser.addEventListener("pointerup", this.onLauncherPointerUp);
    this.teaser.addEventListener("pointercancel", this.onLauncherPointerCancel);
    this.teaser.addEventListener("keydown", (event) => {
      if (event.key !== "Home") return;
      event.preventDefault();
      this.resetLauncherPosition();
    });
    this.launcherReset.addEventListener("click", () => this.resetLauncherPosition());
    this.launcherDismiss.addEventListener("click", () => this.openLauncherDismissal());
    this.launcherDismissalCancel.addEventListener("click", () => this.cancelLauncherDismissal());
    this.launcherDismissalConfirm.addEventListener("click", () => this.confirmLauncherDismissal());
    window.addEventListener("resize", this.onViewportChange);
    window.visualViewport?.addEventListener("resize", this.onViewportChange);
    window.visualViewport?.addEventListener("scroll", this.onViewportChange);
    this.signupForm.addEventListener("submit", (event) => this.signup(event));
    this.emailInput.addEventListener("input", () => {
      this.emailInput.removeAttribute("aria-invalid");
      this.signupError.textContent = "";
    });
    this.spinButton.addEventListener("click", () => this.spin());
    this.copyButton.addEventListener("click", () => this.copyCode());
    this.retryButton.addEventListener("click", () => this.retry());
    this.recoveryButton.addEventListener("click", () => this.recoverAccount());
    this.querySelectorAll("[data-continue]").forEach((link) => link.addEventListener("click", () => this.markClosed()));
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("sigma:modal-closed", () => this.tryPendingOpen());
  }

  async boot() {
    const pageStartedAt = Date.now();
    try {
      await this.loadConfig();
    } catch {
      return;
    }
    this.launcherSuppressed = this.hasSavedLauncherDismissal();
    if (!this.isCampaignOpen() && !this.canRecoverReward()) return;
    const firstSeen = Number(storageRead("localStorage", STORAGE.firstSeen).value) || pageStartedAt;
    storageWrite("localStorage", STORAGE.firstSeen, String(firstSeen));
    if (this.isCampaignOpen()) this.scheduleDeadline();
    if (this.recoveryReturn) {
      this.accountRecovery = true;
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("launch_reward");
      window.history.replaceState(window.history.state, "", cleanUrl);
      this.open(true);
      return;
    }
    if (this.launcherSuppressed) return;
    if (!this.isCampaignOpen()) { this.setLauncherVisible(this.canRecoverReward()); return; }
    const dismissedAt = Number(storageRead("localStorage", STORAGE.dismissed).value);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) {
      this.setLauncherVisible(true);
      return;
    }
    if (this.claimToken) {
      this.open(false);
      return;
    }
    if (this.submitted) { this.setLauncherVisible(true); return; }
    const wait = Math.max(0, firstSeen + DISPLAY_DELAY_MS - Date.now());
    this.priorityTimer = setTimeout(() => this.open(false), wait);
  }

  async loadConfig() {
    const data = await api(this.apiBase, "config");
    const campaign = data.campaign;
    const expectedWeights = new Map([[5, 40], [7, 9], [10, 40], [15, 10], [20, 1]]);
    const rewardPercents = Array.isArray(campaign?.rewards)
      ? campaign.rewards.map((reward) => Number(reward.percent))
      : [];
    const rewardsValid = Array.isArray(campaign?.rewards)
      && campaign.rewards.length === 5
      && new Set(rewardPercents).size === expectedWeights.size
      && campaign.rewards.every((reward) => expectedWeights.get(Number(reward.percent)) === Number(reward.weight));
    if (!campaign || typeof campaign.enabled !== "boolean" || !rewardsValid) throw new Error("Invalid wheel configuration");
    this.campaign = campaign;
    this.signupChallenge = data.signup_challenge;
    this.signupChallengeReceivedAt = Date.now();
    this.protectedSignups = data.protected_signups === true;
    this.recoveryEnabled = data.recovery_enabled === true;
    return campaign;
  }

  isCampaignOpen() {
    const endsAt = Date.parse(this.campaign?.ends_at || FALLBACK_ENDS_AT);
    return Boolean(this.campaign?.enabled && Number.isFinite(endsAt) && Date.now() < endsAt);
  }

  canRecoverReward() {
    return Boolean(this.recoveryEnabled && (this.claimToken || this.submitted || this.accountRecovery || this.recoveryReturn));
  }

  hasSavedLauncherDismissal() {
    const stored = storageRead("localStorage", STORAGE.launcherDismissal);
    if (!stored.value) return false;
    try {
      const preference = JSON.parse(stored.value);
      const campaignId = String(this.campaign?.id || "");
      const endsAt = String(this.campaign?.ends_at || "");
      const cutoff = Date.parse(preference?.endsAt);
      const valid = Boolean(campaignId
        && preference?.campaignId === campaignId
        && preference?.endsAt === endsAt
        && Number.isFinite(cutoff)
        && (Date.now() < cutoff || (preference.recoveryOnly === true
          && this.canRecoverReward() && Date.now() - Number(preference.dismissedAt) < DISMISS_MS)));
      if (!valid) storageRemove("localStorage", STORAGE.launcherDismissal);
      return valid;
    } catch {
      storageRemove("localStorage", STORAGE.launcherDismissal);
      return false;
    }
  }

  scheduleDeadline() {
    const endsAt = Date.parse(this.campaign?.ends_at || FALLBACK_ENDS_AT);
    clearTimeout(this.deadlineTimer);
    this.deadlineTimer = setTimeout(() => this.expire(), Math.min(Math.max(endsAt - Date.now(), 0), 2_147_483_647));
  }

  isSurfaceVisible(surface) {
    if (!(surface instanceof HTMLElement) || surface.hidden || surface.getAttribute("aria-hidden") === "true") return false;
    try {
      const styles = getComputedStyle(surface);
      return styles.display !== "none" && styles.visibility !== "hidden" && surface.getClientRects().length > 0;
    } catch {
      return true;
    }
  }

  hasPrioritySurface() {
    const internationalNotice = document.querySelector("[data-international-tax-notice]");
    if (internationalNotice instanceof HTMLElement && !internationalNotice.hidden) return true;
    const surfaces = [
      document.querySelector("#shopify-pc__banner"),
      ...document.querySelectorAll("#shopify-pc__prefs"),
      ...document.querySelectorAll("dialog[open], [role='dialog'][aria-modal='true']"),
    ];
    return surfaces.some((surface) => !surface?.closest("sigma-launch-wheel") && this.isSurfaceVisible(surface));
  }

  observePrioritySurfaces() {
    if (typeof MutationObserver !== "function" || !document.body) return;
    this.priorityObserver = new MutationObserver(() => this.syncPrioritySurface());
    this.priorityObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["hidden", "style", "class", "open", "aria-hidden", "aria-modal"],
    });
  }

  syncPrioritySurface() {
    if (this.isOpen && this.hasPrioritySurface()) {
      const wasLauncherDismissalConfirmation = this.isLauncherDismissalConfirmation;
      this.lifecycleRevision += 1;
      this.isOpen = false;
      this.isLauncherDismissalConfirmation = false;
      this.pendingLauncherDismissalConfirmation = wasLauncherDismissalConfirmation;
      this.pendingOpen = true;
      this.backdrop.hidden = true;
      clearTimeout(this.pendingConsentTimer);
      this.restoreBackgroundInteraction();
      this.restoreRootScroll();
      clearTimeout(this.priorityTimer);
      this.priorityTimer = setTimeout(() => this.tryPendingOpen(), 500);
      this.syncLauncherVisibility();
      return;
    }
    if (this.isOpen) this.applyBackgroundInert();
    if (this.pendingOpen && !this.hasPrioritySurface()) this.tryPendingOpen();
    this.syncLauncherVisibility();
  }

  setLauncherVisible(visible) {
    this.launcherRequested = Boolean(visible);
    this.syncLauncherVisibility();
  }

  syncLauncherVisibility() {
    if (!this.launcher) return;
    const visible = this.launcherRequested
      && !this.launcherSuppressed
      && !this.isOpen
      && !this.pendingOpen
      && (this.isCampaignOpen() || this.canRecoverReward())
      && !this.hasPrioritySurface();
    const wasHidden = this.launcher.hidden;
    const shouldHide = !visible;
    if (wasHidden !== shouldHide) this.launcher.hidden = shouldHide;
    if (visible && this.savedLauncherPosition && !this.launcher.classList.contains("is-positioned")) {
      const position = this.savedLauncherPosition;
      this.savedLauncherPosition = null;
      this.positionLauncher(position.x, position.y, true);
    } else if (visible && wasHidden && this.launcher.classList.contains("is-positioned")) {
      const left = Number.parseFloat(this.launcher.style.left);
      const top = Number.parseFloat(this.launcher.style.top);
      this.positionLauncher(left, top, true);
    }
  }

  updateLauncherLabel() {
    const label = this.hasClaimedReward || this.submitted || !this.isCampaignOpen() ? "View reward" : "Spin to win";
    if (this.launcherLabel) this.launcherLabel.textContent = label;
    if (this.teaser) this.teaser.setAttribute("aria-label", label);
    if (this.launcherDismiss) this.launcherDismiss.setAttribute("aria-label", this.hasClaimedReward || !this.isCampaignOpen() ? "Hide reward button" : "Hide spin button");
  }

  setClaimedLauncherState(claimed) {
    this.hasClaimedReward = Boolean(claimed);
    if (this.hasClaimedReward && this.claimToken) {
      storageWrite("localStorage", STORAGE.claimed, this.claimToken);
      storageWrite("sessionStorage", STORAGE.claimed, this.claimToken);
    } else {
      storageRemove("localStorage", STORAGE.claimed);
      storageRemove("sessionStorage", STORAGE.claimed);
    }
    this.updateLauncherLabel();
  }

  launcherBounds() {
    const viewport = window.visualViewport;
    const styles = getComputedStyle(this.launcher);
    const safeLeft = Number.parseFloat(styles.getPropertyValue("--wheel-safe-left")) || 0;
    const safeRight = Number.parseFloat(styles.getPropertyValue("--wheel-safe-right")) || 0;
    const safeTop = Number.parseFloat(styles.getPropertyValue("--wheel-safe-top")) || 0;
    const safeBottom = Number.parseFloat(styles.getPropertyValue("--wheel-safe-bottom")) || 0;
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;
    const rect = this.launcher.getBoundingClientRect();
    const minX = viewportLeft + safeLeft + 16;
    const minY = viewportTop + safeTop + 16;
    return {
      minX,
      minY,
      maxX: Math.max(minX, viewportLeft + viewportWidth - safeRight - rect.width - 16),
      maxY: Math.max(minY, viewportTop + viewportHeight - safeBottom - rect.height - 16),
    };
  }

  positionLauncher(x, y, persist = false) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    this.launcher.classList.add("is-positioned");
    this.launcherReset.hidden = false;
    const bounds = this.launcherBounds();
    const left = Math.min(Math.max(x, bounds.minX), bounds.maxX);
    const top = Math.min(Math.max(y, bounds.minY), bounds.maxY);
    this.launcher.style.left = `${left}px`;
    this.launcher.style.top = `${top}px`;
    this.launcher.style.bottom = "auto";
    if (persist) storageWrite("localStorage", STORAGE.launcherPosition, JSON.stringify({ x: left, y: top }));
  }

  restoreLauncherPosition() {
    const raw = storageRead("localStorage", STORAGE.launcherPosition).value;
    if (!raw) return;
    try {
      const position = JSON.parse(raw);
      if (!Number.isFinite(position?.x) || !Number.isFinite(position?.y)) throw new Error("Invalid launcher position");
      this.savedLauncherPosition = position;
    } catch {
      storageRemove("localStorage", STORAGE.launcherPosition);
    }
  }

  resetLauncherPosition() {
    storageRemove("localStorage", STORAGE.launcherPosition);
    this.savedLauncherPosition = null;
    this.launcher.classList.remove("is-positioned", "is-dragging");
    this.launcher.removeAttribute("style");
    this.launcherReset.hidden = true;
    this.teaser.focus();
  }

  handleLauncherPointerDown(event) {
    if (!event.isPrimary || event.button !== 0) return;
    const rect = this.launcher.getBoundingClientRect();
    this.launcherDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      left: rect.left,
      top: rect.top,
      moved: false,
    };
    try { this.teaser.setPointerCapture(event.pointerId); } catch { /* Pointer capture is an enhancement. */ }
  }

  handleLauncherPointerMove(event) {
    const drag = this.launcherDrag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) < 6) return;
    drag.moved = true;
    event.preventDefault();
    this.launcher.classList.add("is-dragging");
    this.positionLauncher(drag.left + deltaX, drag.top + deltaY);
  }

  handleLauncherPointerUp(event) {
    if (!this.launcherDrag || this.launcherDrag.pointerId !== event.pointerId) return;
    this.finishLauncherDrag(this.launcherDrag.moved);
  }

  handleLauncherPointerCancel(event) {
    if (!this.launcherDrag || this.launcherDrag.pointerId !== event.pointerId) return;
    this.finishLauncherDrag(false);
  }

  finishLauncherDrag(persist) {
    const drag = this.launcherDrag;
    if (!drag) return;
    if (persist) {
      const rect = this.launcher.getBoundingClientRect();
      this.positionLauncher(rect.left, rect.top, true);
      this.launcherClickSuppressedUntil = Date.now() + 500;
    }
    try {
      if (this.teaser.hasPointerCapture(drag.pointerId)) this.teaser.releasePointerCapture(drag.pointerId);
    } catch { /* Pointer capture may already be released. */ }
    this.launcher.classList.remove("is-dragging");
    this.launcherDrag = null;
  }

  handleViewportChange() {
    if (this.launcher.hidden || !this.launcher.classList.contains("is-positioned")) return;
    const rect = this.launcher.getBoundingClientRect();
    this.positionLauncher(rect.left, rect.top, true);
  }

  applyBackgroundInert() {
    let activeBranch = this;
    while (activeBranch?.parentElement && activeBranch !== document.body) {
      const parent = activeBranch.parentElement;
      [...parent.children].forEach((sibling) => {
        if (!(sibling instanceof HTMLElement) || sibling === activeBranch || this.backgroundInertStates.has(sibling)) return;
        const state = {
          hadAttribute: sibling.hasAttribute("inert"),
          propertyValue: "inert" in sibling ? sibling.inert : undefined,
        };
        this.backgroundInertStates.set(sibling, state);
        sibling.setAttribute("data-sigma-wheel-inert", "");
        sibling.setAttribute("inert", "");
        if ("inert" in sibling) sibling.inert = true;
      });
      activeBranch = parent;
    }
  }

  restoreBackgroundInteraction() {
    this.backgroundInertStates.forEach((state, element) => {
      if (!(element instanceof HTMLElement) || !element.hasAttribute("data-sigma-wheel-inert")) return;
      element.removeAttribute("data-sigma-wheel-inert");
      if (state.hadAttribute) element.setAttribute("inert", "");
      else element.removeAttribute("inert");
      if ("inert" in element && state.propertyValue !== undefined) element.inert = state.propertyValue;
    });
    this.backgroundInertStates.clear();
  }

  lockRootScroll() {
    if (this.ownsRootScrollLock || document.documentElement.classList.contains("sigma-wheel-scroll-locked")) return;
    document.documentElement.classList.add("sigma-wheel-scroll-locked");
    this.ownsRootScrollLock = true;
  }

  restoreRootScroll() {
    if (!this.ownsRootScrollLock) return;
    document.documentElement.classList.remove("sigma-wheel-scroll-locked");
    this.ownsRootScrollLock = false;
  }

  async open(manual = false) {
    if (!this.pendingOpen) this.lastFocus = document.activeElement;
    if (!this.campaign) {
      try { await this.loadConfig(); } catch {
        if (manual) this.openStatus("Launch reward unavailable", "We could not load the launch reward. Please try again.", true);
        return;
      }
    }
    if (!this.isCampaignOpen() && !this.canRecoverReward()) {
      if (manual) this.openStatus("This launch reward has ended", "Thanks for celebrating our new storefront with us.", false);
      this.setLauncherVisible(false);
      return;
    }
    if (this.launcherSuppressed && !manual && !this.recoveryReturn) return;
    if (this.hasPrioritySurface()) {
      this.pendingOpen = true;
      this.setLauncherVisible(false);
      clearTimeout(this.priorityTimer);
      this.priorityTimer = setTimeout(() => this.tryPendingOpen(), 500);
      return;
    }
    this.pendingOpen = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.isLauncherDismissalConfirmation = false;
    const revision = ++this.lifecycleRevision;
    this.isOpen = true;
    this.backdrop.hidden = false;
    this.setLauncherVisible(false);
    this.lockRootScroll();
    this.applyBackgroundInert();
    if (this.accountRecovery) {
      await this.recoverAccount(revision);
    } else if (this.claimToken) {
      this.showLoading("Loading your reward", "Checking your secure reward session…");
      await this.recover(0, revision);
    }
    else if (this.submitted || !this.isCampaignOpen()) this.showAlreadySpun();
    else this.showScreen("signup", this.emailInput);
  }

  tryPendingOpen() {
    if (!this.pendingOpen) return;
    if (this.hasPrioritySurface()) {
      this.priorityTimer = setTimeout(() => this.tryPendingOpen(), 500);
      return;
    }
    if (this.pendingLauncherDismissalConfirmation) this.openLauncherDismissal();
    else this.open(false);
  }

  close(persistDismissal) {
    if (!this.isOpen) return;
    this.lifecycleRevision += 1;
    clearTimeout(this.pendingConsentTimer);
    clearTimeout(this.priorityTimer);
    this.pendingOpen = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.isLauncherDismissalConfirmation = false;
    this.isOpen = false;
    this.backdrop.hidden = true;
    this.restoreBackgroundInteraction();
    this.restoreRootScroll();
    if (persistDismissal) storageWrite("localStorage", STORAGE.dismissed, String(Date.now()));
    if (this.isCampaignOpen() || this.canRecoverReward()) this.setLauncherVisible(true);
    if (this.lastFocus instanceof HTMLElement && this.lastFocus.isConnected) this.lastFocus.focus();
  }

  requestClose() {
    if (this.isLauncherDismissalConfirmation) this.cancelLauncherDismissal();
    else this.close(true);
  }

  openLauncherDismissal() {
    if (this.launcherSuppressed || (!this.isCampaignOpen() && !this.canRecoverReward())) return;
    if (!this.pendingLauncherDismissalConfirmation) this.lastFocus = this.launcherDismiss;
    if (this.hasPrioritySurface()) {
      this.pendingLauncherDismissalConfirmation = true;
      this.pendingOpen = true;
      this.setLauncherVisible(false);
      clearTimeout(this.priorityTimer);
      this.priorityTimer = setTimeout(() => this.tryPendingOpen(), 500);
      return;
    }
    this.pendingOpen = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.isLauncherDismissalConfirmation = true;
    this.lifecycleRevision += 1;
    this.isOpen = true;
    this.backdrop.hidden = false;
    this.setLauncherVisible(false);
    this.lockRootScroll();
    this.applyBackgroundInert();
    this.launcherDismissalError.textContent = "";
    this.launcherDismissalTitle.textContent = this.hasClaimedReward || !this.isCampaignOpen()
      ? "Hide the reward button?"
      : "Hide the spin button?";
    this.launcherDismissalCopy.textContent = this.hasClaimedReward || !this.isCampaignOpen()
      ? "This hides the button on this browser. Your original discount code remains valid until its original expiry."
      : "If you dismiss this button, you may miss your chance to spin for a launch discount. The offer ends Monday 19 October at 3am UK time.";
    this.launcherDismissalCancel.textContent = this.hasClaimedReward
      ? "Keep reward button"
      : "Keep my chance to spin";
    this.showScreen("launcher-dismissal", this.launcherDismissalCancel);
  }

  cancelLauncherDismissal() {
    if (!this.isLauncherDismissalConfirmation) return;
    this.isLauncherDismissalConfirmation = false;
    this.close(false);
  }

  confirmLauncherDismissal() {
    if (!this.isLauncherDismissalConfirmation) return;
    if (!this.isCampaignOpen() && !this.canRecoverReward()) {
      this.expire();
      return;
    }
    const preference = {
      campaignId: String(this.campaign?.id || ""),
      endsAt: String(this.campaign?.ends_at || ""),
      recoveryOnly: !this.isCampaignOpen(),
      dismissedAt: Date.now(),
    };
    if (!preference.campaignId
      || !Number.isFinite(Date.parse(preference.endsAt))
      || !storageWrite("localStorage", STORAGE.launcherDismissal, JSON.stringify(preference))) {
      this.launcherDismissalError.textContent = "This browser could not save that choice. The spin button is still available.";
      return;
    }
    this.launcherSuppressed = true;
    this.isLauncherDismissalConfirmation = false;
    this.lastFocus = null;
    this.close(false);
    this.setLauncherVisible(false);
    this.focusPageAfterLauncherDismissal();
  }

  focusPageAfterLauncherDismissal() {
    const main = document.querySelector("main");
    if (!(main instanceof HTMLElement)) return;
    const hadTabindex = main.hasAttribute("tabindex");
    if (!hadTabindex) main.setAttribute("tabindex", "-1");
    main.focus({ preventScroll: true });
    if (!hadTabindex && document.activeElement === main) {
      main.addEventListener("blur", () => main.removeAttribute("tabindex"), { once: true });
    }
    else if (!hadTabindex) main.removeAttribute("tabindex");
  }

  markClosed() {
    this.lifecycleRevision += 1;
    clearTimeout(this.pendingConsentTimer);
    this.isOpen = false;
    this.backdrop.hidden = true;
    this.setLauncherVisible(false);
    this.restoreBackgroundInteraction();
    this.restoreRootScroll();
    storageWrite("localStorage", STORAGE.dismissed, String(Date.now()));
  }

  showScreen(name, focusTarget) {
    this.screens.forEach((screen) => { screen.hidden = screen.dataset.screen !== name; });
    const screen = this.screens.find((item) => item.dataset.screen === name);
    const title = screen?.querySelector("h1");
    if (title) title.id = "sigma-wheel-title";
    this.screens.filter((item) => item !== screen).forEach((item) => item.querySelector("h1")?.removeAttribute("id"));
    const focusRevision = this.lifecycleRevision;
    requestAnimationFrame(() => {
      if (!this.isPresentationActive(focusRevision) || screen?.hidden) return;
      (focusTarget || screen?.querySelector("button,input,a") || this.dialog).focus();
    });
  }

  setBusy(button, busy) {
    button.disabled = busy;
    button.setAttribute("aria-busy", String(busy));
  }

  isPresentationActive(revision) {
    return revision === this.lifecycleRevision && this.isOpen && !this.pendingOpen;
  }

  async signup(event) {
    event.preventDefault();
    this.signupError.textContent = "";
    if (!this.emailInput.checkValidity()) {
      this.emailInput.setAttribute("aria-invalid", "true");
      this.signupError.textContent = this.emailInput.value.trim()
        ? "Enter a valid email address to continue."
        : "Enter your email address to continue.";
      this.emailInput.focus();
      return;
    }
    this.emailInput.removeAttribute("aria-invalid");
    if (!this.isCampaignOpen()) return this.expire();
    const revision = this.lifecycleRevision;
    let token = this.claimToken;
    if (!token) {
      token = createRecoveryToken();
      this.setClaimedLauncherState(false);
      const savedLocally = storageWrite("localStorage", STORAGE.claim, token);
      const savedInTab = storageWrite("sessionStorage", STORAGE.claim, token);
      if (!this.claimStorageAvailable || !(savedLocally || savedInTab)) {
        this.signupError.textContent = "This browser cannot safely save your reward session. Allow site storage, then try again.";
        return;
      }
      this.claimToken = token;
    }
    this.setBusy(this.signupButton, true);
    try {
      if (!this.signupChallengeReceivedAt || Date.now() - this.signupChallengeReceivedAt > 25 * 60_000) await this.loadConfig();
      if (this.protectedSignups && Date.now() - this.signupChallengeReceivedAt < 3_000) {
        this.signupError.textContent = "Please wait a moment, then try again.";
        return;
      }
      let browserId = storageRead("localStorage", STORAGE.browser).value;
      if (!/^[A-Za-z0-9_-]{43}$/.test(browserId || "")) {
        browserId = createRecoveryToken();
        if (!storageWrite("localStorage", STORAGE.browser, browserId)) {
          this.signupError.textContent = "Allow site storage to enter the launch wheel.";
          return;
        }
      }
      const data = await post(this.apiBase, "signup", {
        email: this.emailInput.value.trim(),
        marketing_consent: true,
        recovery_token: token,
        ...(this.signupChallenge ? { signup_challenge: this.signupChallenge,
          browser_id: browserId, website: this.querySelector("[data-website]")?.value || "" } : {}),
      });
      if (typeof data.claim_token !== "string" || data.claim_token !== token) throw new Error("Unexpected signup response");
      if (data.state === "pending_consent") {
        if (!this.isPresentationActive(revision)) return;
        this.showLoading("Completing your signup", "We’re confirming your email signup. This should only take a moment.");
        await this.recover(0, revision);
        return;
      }
      if (data.state !== "registered") throw new Error("Unexpected signup response");
      this.submitted = true;
      storageWrite("localStorage", STORAGE.submitted, "true");
      if (!this.isPresentationActive(revision)) return;
      this.showScreen("wheel", this.spinButton);
    } catch (error) {
      if (error.code === "already_claimed") {
        // An email alone never authorizes recovery. Preserve this browser's capability.
        this.submitted = true;
        storageWrite("localStorage", STORAGE.submitted, "true");
        if (!this.isPresentationActive(revision)) return;
        this.showAlreadySpun();
      } else if (!this.isPresentationActive(revision)) return;
      else if (error.code === "campaign_ended") this.expire();
      else if (error.code === "consent_failed") {
        this.signupError.textContent = "Email signup was not accepted. Check your email address and try again with the same reward session.";
      } else if (error.code === "consent_unknown") {
        this.showConsentUnknown();
      }
      else if (error.code === "consent_unavailable" || !error.code || error.code.startsWith("http_")) await this.recover(0, revision);
      else this.signupError.textContent = errorMessage(error.code, error.message);
    } finally {
      this.setBusy(this.signupButton, false);
    }
  }

  showAlreadySpun() {
    this.accountRecovery = true;
    this.showStatus(this.isCampaignOpen() ? "You’ve already spun" : "Spins have ended",
      this.isCampaignOpen()
        ? "Recover your original code while it’s valid. Use the Sigma account with your winning email. You won’t receive another spin."
        : "The launch wheel has closed. If you already won, use the Sigma account with your winning email to recover your original code while it’s valid.", false);
    this.recoveryButton.hidden = false;
    this.showScreen("status", this.recoveryButton);
  }

  async recoverAccount(revision = this.lifecycleRevision) {
    if (!this.isPresentationActive(revision)) return;
    this.accountRecovery = true;
    this.showLoading("Finding your original reward", "Checking your signed-in Sigma account…");
    try {
      const data = await post(this.apiBase, "recover", {});
      if (!this.isPresentationActive(revision)) return;
      if (data.state !== "claimed") throw new Error("Unexpected recovery response");
      await this.showReward(data.reward, false, revision);
    } catch (error) {
      if (!this.isPresentationActive(revision)) return;
      if (error.code === "sign_in_required") {
        this.showStatus("Sign in to recover your code",
          "Use the Sigma account with your winning email. We’ll show your original reward without another spin.", false);
        this.recoveryLogin.hidden = false;
        this.showScreen("status", this.recoveryLogin);
      } else if (error.code === "not_found") {
        this.showStatus("No reward found for this account",
          "We couldn’t find an available reward for your signed-in account. It may have expired or already been used. Check that you’re using the same email you spun with, or contact us for help.", false, true);
      } else {
        this.showStatus("Could not recover your code",
          errorMessage(error.code, "Please try again. Your original reward will not change."), true);
      }
    }
  }

  async recover(pendingAttempt = 0, revision = this.lifecycleRevision) {
    if (!this.isPresentationActive(revision)) return;
    try {
      const data = await post(this.apiBase, "status", { claim_token: this.claimToken });
      if (!this.isPresentationActive(revision)) return;
      if (data.state === "claimed") return this.showReward(data.reward, false, revision);
      if (!this.isCampaignOpen()) { this.showAlreadySpun(); return; }
      if (data.state === "pending_consent") {
        if (pendingAttempt < 3) {
          this.showLoading("Completing your signup", "We’re confirming your email signup. This should only take a moment.");
          clearTimeout(this.pendingConsentTimer);
          this.pendingConsentTimer = setTimeout(() => {
            if (this.isPresentationActive(revision)) this.recover(pendingAttempt + 1, revision);
          }, 700);
        } else {
          this.showStatus(
            "Email signup is taking longer than expected",
            "You can check again without starting a new reward session.",
            true,
          );
        }
        return;
      }
      if (data.state === "email_required") {
        this.setClaimedLauncherState(false);
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Enter your winning email again so we can secure your reward for checkout.";
        return;
      }
      if (data.state === "consent_failed") {
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Email signup was not accepted. Check your email address and try again with the same reward session.";
        return;
      }
      if (data.state === "consent_unknown") {
        this.showConsentUnknown();
        return;
      }
      if (data.state === "reward_unavailable") {
        this.setClaimedLauncherState(false);
        this.showScreen("wheel", this.spinButton);
        this.spinError.textContent = errorMessage("reward_unavailable");
        this.spinButton.textContent = "Try my reserved reward again";
        return;
      }
      if (data.state === "registered") {
        this.setClaimedLauncherState(false);
        this.showScreen("wheel", this.spinButton);
        return;
      }
      this.showStatus("Could not restore your reward", "The reward service returned an unknown state. Please try again.", true);
    } catch (error) {
      if (!this.isPresentationActive(revision)) return;
      if (error.code === "not_found") {
        if (this.submitted || !this.isCampaignOpen()) { this.showAlreadySpun(); return; }
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "We couldn’t find an active reward session. Enter your email to begin again with the same safe session.";
      } else if (error.code === "consent_failed") {
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Email signup was not accepted. Check your email address and try again with the same reward session.";
      } else if (error.code === "consent_unknown") {
        this.showConsentUnknown();
      } else if (error.code === "consent_unavailable") {
        this.showStatus(
          "Email signup could not be confirmed",
          "We could not safely confirm email signup. Check again with the same reward session, or contact us if this continues.",
          true,
        );
      } else this.showStatus("Could not restore your reward", errorMessage(error.code, error.message), true);
    }
  }

  async spin() {
    if (this.spinPending || !this.claimToken) return;
    if (!this.isCampaignOpen()) return this.expire();
    this.spinPending = true;
    const revision = this.lifecycleRevision;
    this.spinError.textContent = "";
    this.setBusy(this.spinButton, true);
    try {
      const data = await post(this.apiBase, "spin", { claim_token: this.claimToken });
      if (!this.isPresentationActive(revision)) return;
      if (data.state === "email_required") {
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Enter your email again to secure your reward.";
        return;
      }
      if (data.state !== "claimed") throw new Error("Unexpected spin response");
      await this.showReward(data.reward, true, revision);
    } catch (error) {
      if (!this.isPresentationActive(revision)) return;
      if (error.code === "email_required") {
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Enter your email again to secure your reward.";
      } else if (error.code === "reward_unavailable") {
        this.spinError.textContent = errorMessage(error.code);
        this.spinButton.textContent = "Try my reserved reward again";
      } else if (error.code === "campaign_ended") this.expire();
      else if (error.code === "consent_failed") {
        this.showScreen("signup", this.emailInput);
        this.signupError.textContent = "Email signup was not accepted. Check your email address and try again with the same reward session.";
      } else if (error.code === "consent_unknown") {
        this.showConsentUnknown();
      }
      else if (error.code === "consent_unavailable") await this.recover(0, revision);
      else if (!error.code || error.code.startsWith("http_")) await this.recoverAfterUncertainSpin(revision);
      else this.spinError.textContent = errorMessage(error.code, error.message);
    } finally {
      this.spinPending = false;
      this.setBusy(this.spinButton, false);
    }
  }

  async recoverAfterUncertainSpin(revision = this.lifecycleRevision) {
    await new Promise((resolve) => setTimeout(resolve, 600));
    if (!this.isPresentationActive(revision)) return;
    try {
      const data = await post(this.apiBase, "status", { claim_token: this.claimToken });
      if (!this.isPresentationActive(revision)) return;
      if (data.state === "claimed") return this.showReward(data.reward, true, revision);
      if (data.state === "email_required") return this.recover(0, revision);
      if (data.state === "pending_consent") return this.recover(0, revision);
      if (data.state === "consent_failed") return this.recover(0, revision);
      if (data.state === "consent_unknown") return this.showConsentUnknown();
      this.spinError.textContent = data.state === "reward_unavailable"
        ? errorMessage("reward_unavailable")
        : "We could not confirm the spin. Try again to continue with the same reward session.";
      this.spinButton.textContent = "Try my reward again";
    } catch (error) {
      if (!this.isPresentationActive(revision)) return;
      this.spinError.textContent = errorMessage(error.code, "We could not confirm the spin. Try again with the same reward session.");
    }
  }

  validateReward(reward) {
    const percent = Number(reward?.percent);
    if (!VALID_REWARDS.has(percent) || typeof reward?.code !== "string" || !reward.code.trim()) throw new Error("Invalid reward response");
    if (!Number.isFinite(Date.parse(reward.expires_at))) throw new Error("Invalid reward expiry");
    return { percent, code: reward.code.trim(), expiresAt: reward.expires_at, emailBound: reward.email_bound === true };
  }

  async showReward(rawReward, animate, revision = this.lifecycleRevision) {
    if (!this.isPresentationActive(revision)) return;
    let reward;
    try { reward = this.validateReward(rawReward); } catch {
      this.showStatus("Reward unavailable", "We could not safely display this reward. Please try again.", true);
      return;
    }
    if (Date.parse(reward.expiresAt) <= Date.now()) {
      this.showStatus("Your reward has expired", "Your original code has reached its expiry date. Recovering a reward does not extend it or give another spin.", false);
      return;
    }
    if (animate) {
      const index = WHEEL_INDEX.get(reward.percent);
      const target = (5 * 360) + 90 - (index * 72);
      this.wheel.classList.add("is-spinning");
      this.wheel.style.setProperty("--wheel-rotation", `${target}deg`);
      if (!matchMedia("(prefers-reduced-motion: reduce)").matches) await new Promise((resolve) => setTimeout(resolve, 3650));
      this.wheel.classList.remove("is-spinning");
      if (!this.isPresentationActive(revision)) return;
    }
    this.querySelector("[data-result-title]").textContent = `You won ${reward.percent}% off!`;
    if (this.accountRecovery) {
      // Account recovery does not turn an unrelated browser token into a capability.
      this.hasClaimedReward = true;
      this.updateLauncherLabel();
    } else this.setClaimedLauncherState(true);
    this.querySelector("[data-code]").textContent = reward.code;
    this.querySelector("[data-copy-feedback]").hidden = true;
    this.copyButton.classList.remove("is-copied");
    this.copyButton.setAttribute("aria-label", "Copy discount code");
    const expiry = this.formatExpiry(reward.expiresAt);
    const recipientNote = reward.emailBound ? " Use the same email you entered here at checkout." : "";
    this.querySelector("[data-result-copy]").textContent = `A little launch treat for your next order. Use your code at checkout ${expiry}. Preorders and selected equipment are excluded. New orders only.${recipientNote}`;
    this.showScreen("result", this.copyButton);
  }

  formatExpiry(value) {
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return "before it expires";
    const formatted = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" }).format(date);
    return `by ${formatted}`;
  }

  async copyCode() {
    const code = this.querySelector("[data-code]").textContent;
    const feedback = this.querySelector("[data-copy-feedback]");
    try {
      await navigator.clipboard.writeText(code);
      feedback.classList.add("sigma-wheel__feedback--announcement");
      feedback.textContent = "Code copied!";
      this.copyButton.classList.add("is-copied");
      this.copyButton.setAttribute("aria-label", "Discount code copied");
      clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = setTimeout(() => {
        this.copyButton.classList.remove("is-copied");
        this.copyButton.setAttribute("aria-label", "Copy discount code");
        feedback.hidden = true;
      }, 1800);
    } catch {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector("[data-code]"));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      feedback.classList.remove("sigma-wheel__feedback--announcement");
      feedback.textContent = "Select Copy to copy your code manually.";
    }
    feedback.hidden = false;
  }

  showConsentUnknown() {
    this.showStatus(
      "We couldn’t confirm your signup",
      "We can’t safely try your email signup again automatically. Contact us and we’ll help with your reward session.",
      false,
      true,
    );
  }

  showStatus(title, copy, retry, support = false) {
    if (!this.isOpen) return;
    this.querySelector("[data-status-title]").textContent = title;
    this.querySelector("[data-status-copy]").textContent = copy;
    this.retryButton.hidden = !retry;
    this.statusSupport.hidden = !support;
    this.recoveryButton.hidden = true;
    this.recoveryLogin.hidden = true;
    const focusTarget = retry
      ? this.retryButton
      : support ? this.statusSupport : this.querySelector("[data-screen='status'] [data-continue]");
    this.showScreen("status", focusTarget);
  }

  openStatus(title, copy, retry) {
    this.pendingOpen = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.isLauncherDismissalConfirmation = false;
    this.lifecycleRevision += 1;
    this.isOpen = true;
    this.backdrop.hidden = false;
    this.setLauncherVisible(false);
    this.lockRootScroll();
    this.applyBackgroundInert();
    this.showStatus(title, copy, retry);
  }

  showLoading(title, copy) {
    this.querySelector("[data-loading-title]").textContent = title;
    this.querySelector("[data-loading-copy]").textContent = copy;
    this.showScreen("loading", this.dialog);
  }

  retry() {
    if (this.accountRecovery) this.recoverAccount();
    else if (this.claimToken) this.recover(0, this.lifecycleRevision);
    else this.open(true);
  }

  expire() {
    this.lifecycleRevision += 1;
    clearTimeout(this.pendingConsentTimer);
    this.campaign = { ...(this.campaign || {}), enabled: false };
    this.pendingOpen = false;
    this.pendingLauncherDismissalConfirmation = false;
    this.isLauncherDismissalConfirmation = false;
    clearTimeout(this.priorityTimer);
    this.finishLauncherDrag(false);
    this.setLauncherVisible(false);
    if (this.canRecoverReward()) {
      this.updateLauncherLabel();
      if (this.isOpen) this.open(true);
      else this.setLauncherVisible(true);
      return;
    }
    if (this.isOpen) this.showStatus("This launch reward has ended", "Thanks for celebrating our new storefront with us.", false);
    else {
      this.restoreBackgroundInteraction();
      this.restoreRootScroll();
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.requestClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...this.dialog.querySelectorAll("button:not([hidden]):not([disabled]),input:not([hidden]):not([disabled]),a[href]:not([hidden])")]
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}

if (!customElements.get("sigma-launch-wheel")) customElements.define("sigma-launch-wheel", SigmaLaunchWheel);
